<template>
    <div class="fixed inset-0 z-50 flex items-start justify-center bg-slate-950/40 px-4 py-6 sm:items-center">
        <div class="absolute inset-0" @click="close"></div>
        <div
            class="relative flex max-h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl border border-emerald-100 bg-white shadow-2xl">
            <div class="border-b border-emerald-100 bg-emerald-50 px-5 py-4">
                <div class="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                    <div>
                        <p class="text-xs font-semibold uppercase tracking-[0.22em] text-emerald-700">Caja · Equipos</p>
                        <h4 class="mt-1 text-lg font-semibold text-slate-800">Seleccionar orden lista para entregar</h4>
                        <p class="mt-1 text-sm text-slate-600">
                            Elige una orden para cargarla en la venta actual como una linea normal.
                        </p>
                    </div>
                    <button type="button"
                        class="inline-flex items-center justify-center rounded-lg border border-emerald-200 bg-white px-3 py-2 text-sm font-semibold text-slate-600 transition hover:border-emerald-400 hover:text-emerald-700"
                        @click="close">
                        Cerrar
                    </button>
                </div>
            </div>

            <div class="grid min-h-0 flex-1 gap-0 lg:grid-cols-[minmax(0,340px)_minmax(0,1fr)]">
                <aside class="border-b border-emerald-100 bg-slate-50/70 p-4 lg:border-b-0 lg:border-r">
                    <div
                        class="grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto] lg:grid-cols-1 xl:grid-cols-[minmax(0,1fr)_auto]">
                        <input ref="searchInput" v-model="customerSearch" type="text"
                            class="w-full rounded-lg border-2 border-emerald-100 bg-white px-3 py-2 text-sm text-slate-700 outline-none focus:border-emerald-500"
                            placeholder="Nombre del cliente (obligatorio)" @keydown.enter.prevent="loadOrders" />
                        <button type="button"
                            class="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-emerald-300"
                            :disabled="loading" @click="loadOrders">
                            {{ loading ? "Buscando..." : "Buscar" }}
                        </button>
                    </div>

                    <p class="mt-2 text-xs text-slate-500">
                        Filtra por cliente para evitar mezclar ordenes de personas distintas.
                    </p>

                    <p v-if="searchValidationMessage"
                        class="mt-2 rounded-lg bg-amber-50 px-3 py-2 text-xs font-medium text-amber-700">
                        {{ searchValidationMessage }}
                    </p>

                    <div class="mt-3 flex items-center justify-between text-xs text-slate-500">
                        <span>{{ totalOrders }} orden(es) encontrada(s)</span>
                        <button type="button"
                            class="rounded-md border border-emerald-200 bg-white px-2.5 py-1 font-semibold text-emerald-700 transition hover:border-emerald-400"
                            :disabled="loading" @click="loadOrders">
                            Actualizar
                        </button>
                    </div>

                    <div class="mt-4 overflow-y-auto pr-1 lg:max-h-[60vh]">
                        <div v-if="orders.length" class="overflow-hidden rounded-xl border border-emerald-100 bg-white">
                            <table class="min-w-full border-separate text-xs" style="border-spacing: 0 8px;">
                                <thead class="bg-emerald-50 text-slate-600">
                                    <tr>
                                        <th class="px-3 py-2 text-left font-semibold uppercase tracking-[0.12em]">Orden
                                        </th>
                                        <th class="px-3 py-2 text-left font-semibold uppercase tracking-[0.12em]">
                                            Cliente</th>
                                        <th class="px-3 py-2 text-left font-semibold uppercase tracking-[0.12em]">Equipo
                                        </th>
                                        <th class="px-3 py-2 text-left font-semibold uppercase tracking-[0.12em]">Estado
                                        </th>
                                        <th class="px-3 py-2 text-right font-semibold uppercase tracking-[0.12em]">
                                            Pendiente</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="order in orders" :key="order.id" class="cursor-pointer transition"
                                        :class="selectedOrder?.id === order.id ? 'bg-emerald-50/80' : 'bg-white hover:bg-slate-50'"
                                        @click="selectOrder(order)">
                                        <td
                                            class="rounded-l-lg border border-r-0 border-emerald-100 px-3 py-2 align-top">
                                            <div class="font-semibold text-slate-800">{{ getOrderCode(order) }}</div>
                                            <div class="mt-1 text-[11px] text-slate-500">{{
                                                formatDate(order.fecha_recepcion) }}</div>
                                        </td>
                                        <td
                                            class="border border-x-0 border-emerald-100 px-3 py-2 align-top text-slate-700">
                                            {{ getOrderCustomerName(order) }}
                                        </td>
                                        <td
                                            class="border border-x-0 border-emerald-100 px-3 py-2 align-top text-slate-700">
                                            {{ buildDeviceDescription(order) }}
                                        </td>
                                        <td class="border border-x-0 border-emerald-100 px-3 py-2 align-top">
                                            <span
                                                class="rounded-full bg-amber-100 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-amber-700">
                                                {{ formatStatus(order.estado) }}
                                            </span>
                                        </td>
                                        <td
                                            class="rounded-r-lg border border-l-0 border-emerald-100 px-3 py-2 text-right align-top font-semibold text-rose-700">
                                            {{ formatMoney(buildPendingAmount(order)) }}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <div v-else-if="!loading"
                            class="rounded-xl border border-dashed border-emerald-200 bg-white px-4 py-8 text-center text-sm text-slate-500">
                            No hay equipos de taller con esos criterios.
                        </div>
                    </div>
                </aside>

                <section class="min-h-0 overflow-y-auto p-5">
                    <div v-if="selectedOrder" class="space-y-5">
                        <div class="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
                            <div>
                                <p class="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-700">Orden
                                    seleccionada</p>
                                <h5 class="mt-1 text-xl font-semibold text-slate-800">{{ getOrderCode(selectedOrder) }}
                                </h5>
                                <p class="mt-1 text-sm text-slate-500">{{ getOrderCustomerName(selectedOrder) }}</p>
                            </div>
                            <span
                                class="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.15em] text-slate-600">
                                {{ formatStatus(selectedOrder.estado) }}
                            </span>
                        </div>

                        <div class="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                            <div class="rounded-xl bg-slate-50 p-4">
                                <p class="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Equipo
                                </p>
                                <p class="mt-2 text-sm font-semibold text-slate-800">{{
                                    buildDeviceDescription(selectedOrder) }}</p>
                            </div>
                            <div class="rounded-xl bg-slate-50 p-4">
                                <p class="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Servicio
                                </p>
                                <p class="mt-2 text-sm font-semibold text-slate-800">{{
                                    getOrderServiceName(selectedOrder) }}</p>
                            </div>
                            <div class="rounded-xl bg-slate-50 p-4">
                                <p class="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                                    Recepcion</p>
                                <p class="mt-2 text-sm font-semibold text-slate-800">{{
                                    formatDate(selectedOrder.fecha_recepcion) }}</p>
                            </div>
                        </div>

                        <div class="grid gap-3 md:grid-cols-3">
                            <div class="rounded-xl bg-slate-50 p-4">
                                <p class="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Total
                                    estimado</p>
                                <p class="mt-2 text-lg font-semibold text-slate-800">{{
                                    formatMoney(buildEstimatedAmount(selectedOrder)) }}</p>
                            </div>
                            <div class="rounded-xl bg-blue-50 p-4">
                                <p class="text-[11px] font-semibold uppercase tracking-[0.18em] text-blue-700">Abonado
                                </p>
                                <p class="mt-2 text-lg font-semibold text-blue-800">{{
                                    formatMoney(selectedOrder.monto_pagado_total) }}</p>
                            </div>
                            <div class="rounded-xl bg-rose-50 p-4">
                                <p class="text-[11px] font-semibold uppercase tracking-[0.18em] text-rose-700">Saldo a
                                    cargar</p>
                                <p class="mt-2 text-lg font-semibold text-rose-800">{{
                                    formatMoney(buildPendingAmount(selectedOrder)) }}</p>
                            </div>
                        </div>

                        <div class="rounded-xl border border-emerald-100 bg-emerald-50/70 p-4 text-sm text-slate-600">
                            <p class="font-semibold text-emerald-700">Como se cargara en la venta</p>
                            <p class="mt-2">
                                Al confirmar, esta orden se agregara al carrito como una linea normal usando el saldo
                                pendiente actual. Luego puedes sumar otros productos en la misma cuenta y cobrar todo
                                con el flujo normal de ventas.
                            </p>
                        </div>
                    </div>

                    <div v-else
                        class="flex min-h-[280px] items-center justify-center rounded-xl border border-dashed border-emerald-200 bg-emerald-50/60 px-6 text-center text-sm text-slate-500">
                        Selecciona una orden del panel izquierdo para cargarla en la venta.
                    </div>
                </section>
            </div>

            <div class="border-t border-emerald-100 bg-white px-5 py-4">
                <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-end">
                    <button type="button"
                        class="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-600 transition hover:border-slate-300 hover:text-slate-800"
                        @click="close">
                        Cancelar
                    </button>
                    <button type="button"
                        class="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-emerald-300"
                        :disabled="!selectedOrder" @click="confirmSelection">
                        Cargar a la venta
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import api from "@/api";
import { errorActions } from "@/alerts";
import { idLocalStore } from "@/publicjs/localStorage";
import { nextTick } from "vue";

export default {
    name: "SearchWorkshopReadyOrders",
    emits: ["send-data", "close"],
    data() {
        return {
            loading: false,
            orders: [],
            totalOrders: 0,
            customerSearch: "",
            searchValidationMessage: "",
            selectedOrder: null,
        };
    },
    async mounted() {
        await this.loadOrders();
        await nextTick();
        this.$refs.searchInput?.focus();
    },
    methods: {
        async loadOrders() {
            this.loading = true;

            try {
                const normalizedCustomerSearch = this.customerSearch.trim();
                if (normalizedCustomerSearch.length < 2) {
                    this.searchValidationMessage = "Escribe al menos 2 letras del nombre del cliente para buscar.";
                    this.orders = [];
                    this.totalOrders = 0;
                    this.selectedOrder = null;
                    return;
                }

                this.searchValidationMessage = "";
                const storeId = await idLocalStore();
                const params = new URLSearchParams({
                    idsucursal: String(storeId || 0),
                    per_page: "50",
                    status: "LISTO_PARA_ENTREGA",
                });

                params.set("search", normalizedCustomerSearch);

                const response = await api.get(`taller/ordenes?${params.toString()}`);
                const payload = response.data || {};
                this.orders = payload?.data || [];
                this.totalOrders = Number(payload?.total || this.orders.length || 0);

                if (this.selectedOrder?.id) {
                    this.selectedOrder = this.orders.find((order) => order.id === this.selectedOrder.id) || null;
                }
            } catch (error) {
                errorActions(error);
            } finally {
                this.loading = false;
            }
        },
        selectOrder(order) {
            this.selectedOrder = order;
        },
        confirmSelection() {
            if (!this.selectedOrder) {
                return;
            }

            this.$emit("send-data", this.selectedOrder);
        },
        close() {
            this.$emit("close");
        },
        getOrderCode(order) {
            return order?.codigo || "Sin codigo";
        },
        getOrderCustomerName(order) {
            return order?.customer?.nombres || "Cliente no disponible";
        },
        getOrderServiceName(order) {
            if (Array.isArray(order?.details) && order.details.length) {
                return order.details.map((detail) => detail?.descripcion).filter(Boolean).join(" + ");
            }

            return order?.articulo_referencia || "Servicio no disponible";
        },
        buildEstimatedAmount(order) {
            if (order?.presupuesto !== null && order?.presupuesto !== undefined) {
                return Number(order.presupuesto || 0);
            }

            const details = Array.isArray(order?.details) ? order.details : [];
            const detailsTotal = details.reduce((accumulator, detail) => {
                const quantity = Number(detail?.cantidad || 0);
                const unitPrice = Number(detail?.precio_unitario || 0);
                return accumulator + (Number.isFinite(quantity) && Number.isFinite(unitPrice)
                    ? Math.max(quantity, 0) * Math.max(unitPrice, 0)
                    : 0);
            }, 0);

            if (detailsTotal > 0) {
                return detailsTotal;
            }

            if (order?.precio_articulo_referencia !== null && order?.precio_articulo_referencia !== undefined) {
                return Number(order.precio_articulo_referencia || 0);
            }

            return Number(order?.precio_chequeo || 0);
        },
        buildPendingAmount(order) {
            return Math.max(this.buildEstimatedAmount(order) - Number(order?.monto_pagado_total || 0), 0);
        },
        buildDeviceDescription(order) {
            return [order?.tipo_equipo, order?.marca, order?.modelo].filter(Boolean).join(" - ") || "Equipo sin descripcion";
        },
        formatStatus(value) {
            return String(value || "")
                .replaceAll("_", " ")
                .toLowerCase()
                .replace(/(^|\s)\S/g, (letter) => letter.toUpperCase());
        },
        formatDate(value) {
            if (!value) {
                return "No disponible";
            }

            return new Intl.DateTimeFormat("es-DO", {
                dateStyle: "medium",
                timeStyle: "short",
            }).format(new Date(value));
        },
        formatMoney(value) {
            return new Intl.NumberFormat("es-DO", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            }).format(Number(value || 0));
        },
    },
};
</script>
